import { appConfigStore } from 'Models/AppConfigStore';
import { checkIfInvalidName } from 'Util/StringUtils';
import { IsValidPhone } from 'Validators/Functions/HumanWritten/Phone';
import { isEmail } from 'Validators/Functions/Email';
import { serialiseDate } from 'Util/AttributeUtils';
import { isNotNullOrUndefined, isNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import {
	BookingFormState,
	IBookingFormState,
} from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/context/BookingFormState';
import { VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { PassengerBookingWizardData } from '../../BookingWizardData/PassengerBookingWizardData';
import { PassengerDetail } from '../../BookingWizardData/PassengerDetail';

/**
 * This function will return the TOTAL passenger count from the booking wizard data that is passed into the function.
 * @param wizardData - The booking wizard data to get the passenger count from.
 */
export const getTotalPassengerCountFromWizardData = (wizardData: BookingWizardData) => {
	if (
		wizardData.innerWizardData instanceof PassengerBookingWizardData ||
		wizardData.innerWizardData instanceof VehicleBookingWizardData
	) {
		return (
			wizardData.innerWizardData.adultTickets.length +
			wizardData.innerWizardData.childTickets.length +
			wizardData.innerWizardData.infantTickets.length +
			(wizardData.innerWizardData.passengerDTickets?.length ?? 0) +
			(wizardData.innerWizardData.passengerETickets?.length ?? 0) +
			(wizardData.innerWizardData.passengerFTickets?.length ?? 0) +
			(wizardData.innerWizardData.passengerGTickets?.length ?? 0) +
			(wizardData.innerWizardData.passengerHTickets?.length ?? 0)
		);
	}
	return 0;
};

/**
 * This function will return the TOTAL passenger count from the booking wizard data that is passed into the function.
 * @param wizardData - The booking wizard data to get the passenger count from.
 */
export const combineAllPassengerTickets = (data: BookingWizardData | IBookingFormState): PassengerDetail[] => {
	if (
		data instanceof BookingWizardData &&
		(data.innerWizardData instanceof PassengerBookingWizardData ||
			data.innerWizardData instanceof VehicleBookingWizardData)
	) {
		return [
			...(data.innerWizardData.adultTickets ?? []),
			...(data.innerWizardData.childTickets ?? []),
			...(data.innerWizardData.infantTickets ?? []),
			...(data.innerWizardData.passengerDTickets ?? []),
			...(data.innerWizardData.passengerETickets ?? []),
			...(data.innerWizardData.passengerFTickets ?? []),
			...(data.innerWizardData.passengerGTickets ?? []),
			...(data.innerWizardData.passengerHTickets ?? []),
		];
	}
	if (data instanceof BookingFormState) {
		return [
			...(data.adultTickets ?? []),
			...(data.childTickets ?? []),
			...(data.infantTickets ?? []),
			...(data.passengerDTickets ?? []),
			...(data.passengerETickets ?? []),
			...(data.passengerFTickets ?? []),
			...(data.passengerGTickets ?? []),
			...(data.passengerHTickets ?? []),
		];
	}
	return [];
};

export const filterPassengerTicketsByCompleteness = (passengerTickets: PassengerDetail[], returnCompleted: boolean) => {
	const errors = validatePassengers(passengerTickets);
	return passengerTickets.filter(x => {
		const isCompleted = errors?.find(y => y.id === x.id)?.errors === undefined;
		return returnCompleted ? isCompleted : !isCompleted;
	});
};

export const completedPassengerTickets = (passengerTickets: PassengerDetail[]): PassengerDetail[] => {
	return filterPassengerTicketsByCompleteness(passengerTickets, true);
};

export const incompletePassengerTickets = (passengerTickets: PassengerDetail[]): PassengerDetail[] => {
	return filterPassengerTicketsByCompleteness(passengerTickets, false);
};

export const passengerTicketIsEmpty = (passenger: PassengerDetail) => {
	return (
		stringIsEmpty(passenger.firstName) &&
		stringIsEmpty(passenger.lastName) &&
		stringIsEmpty(passenger.phone) &&
		stringIsEmpty(passenger.email) &&
		isNullOrUndefined(passenger.age) &&
		stringIsEmpty(passenger.address) &&
		stringIsEmpty(passenger.gender) &&
		isNullOrUndefined(passenger.dateOfBirth)
	);
};

export interface PassengerError {
	id: string;
	errors: {
		[key: string]: string; // Key-value pairs for field and error message
	};
}

export function validatePassengers(passengers: PassengerDetail[]): PassengerError[] | null {
	const allErrors: PassengerError[] = [];

	passengers.forEach(passenger => {
		const passengerErrors = validatePassenger(passenger);

		if (passengerErrors) {
			allErrors.push(passengerErrors);
		}
	});

	// Return the errors only if there are any
	return allErrors.length > 0 ? allErrors : null;
}

export function validatePassenger(passenger: PassengerDetail): PassengerError | null {
	const errors: { [key: string]: string } = {};
	// The primary-contact passenger carries the booking's "name on booking", so its first/last name
	// is required whenever that field is enabled — even if passenger names are otherwise optional.
	const isPrimaryContact = !!passenger.isPrimaryContact && appConfigStore.collectPrimaryBookingNameEnabled;
	// Validation logic
	if (appConfigStore.passengerFirstNameEnabled) {
		if (
			stringIsEmpty(passenger.firstName) &&
			(appConfigStore.passengerFirstNameRequired ||
				(isPrimaryContact && appConfigStore.primaryBookingFirstNameEnabled))
		) {
			errors.firstName = 'Required field';
		}

		if (checkIfInvalidName(passenger.firstName ?? '')) {
			errors.firstName = "No spaces. A-Z and '-' only.";
		}
	}

	if (appConfigStore.passengerLastNameEnabled) {
		if (
			stringIsEmpty(passenger.lastName) &&
			(appConfigStore.passengerLastNameRequired ||
				(isPrimaryContact && appConfigStore.primaryBookingLastNameEnabled))
		) {
			errors.lastName = 'Required field';
		}

		if (checkIfInvalidName(passenger.lastName ?? '')) {
			errors.lastName = "No spaces. A-Z and '-' only.";
		}
	}

	if (appConfigStore.passengerPhoneEnabled) {
		if (stringIsEmpty(passenger.phone) && appConfigStore.passengerPhoneRequired) {
			errors.phone = 'Required field';
		} else if (stringNotEmpty(passenger.phone) && !IsValidPhone(passenger.phone)) {
			errors.phone = 'Enter a valid phone number';
		}
	}

	if (appConfigStore.passengerEmailEnabled) {
		if (
			stringIsEmpty(passenger.email) &&
			appConfigStore.passengerEmailRequired &&
			appConfigStore.passengerEmailEnabled
		) {
			errors.email = 'Required field';
		} else if (stringNotEmpty(passenger.email) && !isEmail(passenger.email)) {
			errors.email = 'Enter a valid email address (xx@xx.xx)';
		}
	}

	if (isNullOrUndefined(passenger.age) && appConfigStore.passengerAgeRequired && appConfigStore.passengerAgeEnabled) {
		errors.age = 'Required field';
	}

	if (
		stringIsEmpty(passenger.address) &&
		appConfigStore.passengerAddressRequired &&
		appConfigStore.passengerAddressEnabled
	) {
		errors.address = 'Required field';
	}

	if (
		stringIsEmpty(passenger.gender) &&
		appConfigStore.passengerGenderRequired &&
		appConfigStore.passengerGenderEnabled
	) {
		errors.gender = 'Required field';
	}

	if (
		isNullOrUndefined(passenger.dateOfBirth) &&
		appConfigStore.passengerDateOfBirthRequired &&
		appConfigStore.passengerDateOfBirthEnabled
	) {
		errors.dateOfBirth = 'Required field';
	}

	return Object.keys(errors).length > 0 ? { id: passenger.id, errors } : null;
}

/**
 * This function will return the list of passengers with serialised date of birth.
 * @param passengers - The list of passengers to serialise the date of birth for.
 */
export const serialisePassengerDOB = (passengers?: PassengerDetail[]) => {
	if (isNullOrUndefined(passengers) || passengers.length === 0) {
		return [];
	}
	return (
		passengers.map(ticket => ({
			...ticket,
			dateOfBirth: isNotNullOrUndefined(ticket.dateOfBirth) ? serialiseDate(ticket.dateOfBirth) : null,
		})) ?? []
	);
};
